import { createAction, createAsyncThunk } from '@reduxjs/toolkit'
import type {
  GroupRecipientList,
  GroupRecipientListInit,
  ListResult,
  GroupRecipientListUpdate,
  GroupRecipientListSortableField,
} from 'common/api/v1/types'

import { ThunkApi } from '../../store'
import { enqueueErrorSnackbar, enqueueSuccessSnackbar } from './notificationActions'
import { withDefaultPagination } from '../../utils'
import { PaginatedRequestParams } from '../../api/nm-types'

export const createGroupList = createAsyncThunk<
  GroupRecipientList,
  GroupRecipientListInit & Pick<GroupRecipientListUpdate, 'addGroups'>,
  ThunkApi
>('groupLists/createGroupList', async (groupList, { dispatch, extra: { routes, api, navigate } }) => {
  try {
    const res = await api.groupListsApi.createGroupList(groupList)
    navigate()(routes.groupLists())
    dispatch(enqueueSuccessSnackbar(`Created group list ${groupList.name}`))
    return res
  } catch (err) {
    dispatch(enqueueErrorSnackbar({ error: err, operation: 'create group list' }))
    throw err
  }
})

export const removeGroupList = createAsyncThunk<GroupRecipientList, GroupRecipientList['id'], ThunkApi>(
  'groupList/removeGroupList',
  async (groupListId, { dispatch, extra: { routes, api, navigate } }) => {
    try {
      const res = await api.groupListsApi.removeGroupList(groupListId)
      navigate()(routes.groupLists())
      dispatch(enqueueSuccessSnackbar(`Removed group list: ${res.name}`))
      return res
    } catch (err) {
      dispatch(enqueueErrorSnackbar({ error: err, operation: 'delete group list' }))
      throw err
    }
  },
)

export const updateGroupList = createAsyncThunk<
  GroupRecipientList,
  {
    groupList: GroupRecipientList & GroupRecipientListUpdate
    withRedirection: boolean
  },
  ThunkApi
>(
  'groupLists/updateGroupList',
  async ({ groupList, withRedirection }, { dispatch, extra: { routes, api, navigate } }) => {
    try {
      const res = await api.groupListsApi.updateGroupList(groupList.id, groupList)
      withRedirection && navigate()(routes.groupLists())
      dispatch(enqueueSuccessSnackbar(`Edited group list: ${groupList.name}`))
      return res
    } catch (err) {
      dispatch(enqueueErrorSnackbar({ error: err, operation: 'update group list' }))
      throw err
    }
  },
)

export const getGroupLists = createAsyncThunk<
  ListResult<GroupRecipientList>,
  Partial<PaginatedRequestParams<GroupRecipientListSortableField>>,
  ThunkApi
>('groupLists/getGroupLists', async (params, { dispatch, extra: { api } }) => {
  try {
    return await api.groupListsApi.getGroupLists(withDefaultPagination(params))
  } catch (error) {
    dispatch(enqueueErrorSnackbar({ error, operation: 'get group lists' }))
    throw error
  }
})

export const getGroupList = createAsyncThunk<GroupRecipientList, GroupRecipientList['id'], ThunkApi>(
  'groupLists/getGroupList',
  async (id, { dispatch, extra: { api } }) => {
    try {
      return await api.groupListsApi.getGroupList(id)
    } catch (error) {
      dispatch(enqueueErrorSnackbar({ error, operation: 'get group list' }))
      throw error
    }
  },
)

export const clearGroupList = createAction('groupLists/clearGroupList')
